/**
 * One deal-screening run — the memo the run page is about.
 *
 * The same contract as `useMonitoringRun`: a refresh holds the run on screen
 * and marks it `stale` so the page dims it rather than blanking to a skeleton,
 * and a run the server says is `generating` is re-read every 5 s (generation is
 * a background job with no push channel). That poll never sets `stale` — a
 * page that dims itself every 5 s reads as flicker, not as feedback.
 *
 * Writes on this page (a manual value, a conflict choice, "Mark reviewed")
 * answer with the whole recomputed run, so `replace` takes that answer as the
 * new state instead of paying a second request to learn the same thing.
 *
 * A write can answer with ANOTHER run: a change applied from the chat is
 * saved as the deal's next version (DOP-653). The page takes that answer and
 * then names it in the URL. The run id changing to the run already on screen
 * is not a new memo to load: nothing is blanked and nothing is fetched, so
 * the memo changes in place and whatever is open beside it stays open.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { dealScreeningService } from "../services/dealScreeningService";
import type { DealScreeningRun } from "../types/dealScreening";
import { isRateLimited } from "../utils/isRateLimited";

/** Why a read runs: the first paint, a refresh someone asked for, or the background poll. */
type LoadMode = "initial" | "refresh" | "poll";

/** How often a generating run is re-read. */
export const DEAL_SCREENING_POLL_MS = 5000;

export interface DealScreeningRunData {
	run: DealScreeningRun | null;
	/** Nothing to show yet — the first load for this run id. */
	loading: boolean;
	error: Error | undefined;
	/** A refetch is in flight and the run on screen is the PREVIOUS answer. */
	stale: boolean;
	refresh: () => Promise<void>;
	/** Take a write's response as the run on screen. */
	replace: (next: DealScreeningRun) => void;
}

export function useDealScreeningRun(
	runId: string | undefined,
): DealScreeningRunData {
	const [run, setRun] = useState<DealScreeningRun | null>(null);
	const [loading, setLoading] = useState(true);
	const [stale, setStale] = useState(false);
	const [error, setError] = useState<Error | undefined>(undefined);
	// A 429 stops the poll. Polling into a spent budget only keeps it spent;
	// the next read that succeeds (a manual refresh, a write's re-read) resumes it.
	const [rateLimited, setRateLimited] = useState(false);

	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// Rising counter: a slow answer for the previous run id (or an older
	// refetch) cannot overwrite the current one.
	const requestId = useRef(0);
	// The run on screen, for telling a new memo from the one a write handed over.
	const onScreen = useRef<DealScreeningRun | null>(null);

	const load = useCallback(
		async (mode: LoadMode) => {
			const initial = mode === "initial";
			if (!runId) return;
			const id = ++requestId.current;
			if (initial) setLoading(true);
			// A poll is silent: the list changes without the user doing anything,
			// so dimming it every 5 s read as a flicker, not as feedback.
			else if (mode === "refresh") setStale(true);
			try {
				const next = await dealScreeningService.getRun(runId);
				if (!mounted.current || id !== requestId.current) return;
				onScreen.current = next;
				setRun(next);
				setError(undefined);
				setRateLimited(false);
			} catch (caught) {
				if (!mounted.current || id !== requestId.current) return;
				const failure = caught instanceof Error ? caught : new Error(String(caught));
				setError(failure);
				if (isRateLimited(failure)) setRateLimited(true);
				if (initial) {
					onScreen.current = null;
					setRun(null);
				}
			} finally {
				if (mounted.current && id === requestId.current) {
					setLoading(false);
					setStale(false);
				}
			}
		},
		[runId],
	);

	useEffect(() => {
		// The run a write just handed over, now named in the URL: it is
		// already on screen, whole and current.
		if (runId && onScreen.current?.public_id === runId) {
			setLoading(false);
			return;
		}
		// Another run is another memo: never dim deal A's prose under deal B.
		onScreen.current = null;
		setRun(null);
		setError(undefined);
		setLoading(true);
		void load("initial");
		// `load` changes with the run id and with nothing else.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [load]);

	const refresh = useCallback(() => load("refresh"), [load]);

	const replace = useCallback((next: DealScreeningRun) => {
		// Claims the newest answer, so a poll already in flight cannot land
		// after it and put the pre-write run back.
		requestId.current += 1;
		onScreen.current = next;
		setRun(next);
		setError(undefined);
		setStale(false);
	}, []);

	const generating = run?.generating ?? false;
	useEffect(() => {
		if (!generating || !runId || rateLimited) return;
		const timer = window.setInterval(() => {
			void load("poll");
		}, DEAL_SCREENING_POLL_MS);
		return () => window.clearInterval(timer);
	}, [generating, runId, rateLimited, load]);

	return { run, loading, error, stale, refresh, replace };
}
